import { Injectable } from '@angular/core';

import { Event, EventNote, EventNoteFilters, EventPartCalendarEntry, EventPartCalendarFilters } from '../../models/event';
import { ListResponse } from '../../models/list-response';
import { ApiService } from '../api/api.service';

@Injectable({
  providedIn: 'root'
})
export class EventService {
  /**
   * A service providing access to events via the API.
   */

  constructor(
    private apiService: ApiService
  ) {}

  /**
   * Get a page of events from the API.
   */
  getEvents(pageNumber?: number,
            pageSize?: number): Promise<ListResponse<Event>> {
    return this.apiService.getCollectionNoFilters<Event>(
      'events/',
      pageNumber,
      pageSize
    );
  }

  /**
   * Get an individual event from the API.
   */
  getEvent(id: number): Promise<Event> {
    return this.apiService.getItem<Event>(
      'events/',
      id
    );
  }

  /**
   * Cancel an event via the API.
   */
  cancelEvent(eventId: number,
              sendCancellationEmails: boolean,
              cancellationEmailText: string): Promise<void> {
    return this.apiService.postToAPI<any>(
      'gym-endpoints/cancel-event/',
      {
        event_id: eventId,
        send_cancellation_emails: sendCancellationEmails,
        cancellation_email_text: cancellationEmailText
      }
    ).then();
  }

  /**
   * Cancel a private event via the API, optionally refunding part or all of
   * the amount paid on its booking.
   */
  cancelPrivateEvent(eventId: number,
                     refundCurrencyAmount: number,
                     refundVoucherCount: number,
                     sendCancellationEmails: boolean,
                     cancellationEmailText: string): Promise<void> {
    return this.apiService.postToAPI<any>(
      'gym-endpoints/cancel-private-event/',
      {
        event_id: eventId,
        refund_currency_amount: refundCurrencyAmount,
        refund_voucher_count: refundVoucherCount,
        send_cancellation_emails: sendCancellationEmails,
        cancellation_email_text: cancellationEmailText
      }
    ).then();
  }

  setEventPartInstructors(eventPartId: number, instructors: {id: number, is_sub: boolean}[]): Promise<void> {
    return this.apiService.postToAPI<any>(
      'gym-endpoints/set-event-part-instructors/',
      {
        event_part_id: eventPartId,
        instructors
      }
    ).then();
  }

  getEventNotes(pageNumber?: number,
                pageSize?: number,
                filters?: EventNoteFilters): Promise<ListResponse<EventNote>> {
    return this.apiService.getCollection<EventNote, EventNoteFilters>(
      'event-notes/',
      pageNumber,
      pageSize,
      filters
    )
  }

  getEventNote(id: number): Promise<EventNote> {
    return this.apiService.getItem<EventNote>(
      'event-notes/',
      id
    );
  }

  addEventNote(note: EventNote): Promise<EventNote> {
    return this.apiService.postToAPI<EventNote>(
      'event-notes/',
      note
    );
  }

  updateEventNote(note: EventNote): Promise<EventNote> {
    return this.apiService.patchToAPI<EventNote>(
      note.url,
      note
    );
  }

  deleteEventNote(note: EventNote): Promise<EventNote> {
    return this.apiService.deleteFromAPI<EventNote>(
      note.url
    );
  }

  getEventPartCalendar(pageNumber?: number,
                       pageSize?: number,
                       filters?: EventPartCalendarFilters): Promise<ListResponse<EventPartCalendarEntry>> {
    return this.apiService.getCollection<EventPartCalendarEntry, EventPartCalendarFilters>(
      'event-part-calendar/',
      pageNumber,
      pageSize,
      filters
    );
  }
}
